/**
 * OfflineBanner - Detecta estado offline e mostra banner
 * Permite visualização de dados em cache enquanto offline
 */

import { useState, useEffect } from "react";
import { WifiOff, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return isOnline;
}

export function OfflineBanner() {
  const isOnline = useOnlineStatus();
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissed state when coming back online
  useEffect(() => {
    if (isOnline) {
      setDismissed(false);
    }
  }, [isOnline]);

  if (isOnline || dismissed) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4">
      <div className="flex items-center gap-3 bg-amber-500 text-amber-950 px-4 py-2 rounded-lg shadow-lg">
        <WifiOff className="h-5 w-5 flex-shrink-0" />
        <span className="text-sm font-medium">
          Você está offline. Dados em cache estão sendo exibidos.
        </span>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-amber-950 hover:bg-amber-600 hover:text-amber-950"
          onClick={() => window.location.reload()}
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
        <button
          onClick={() => setDismissed(true)}
          className="text-amber-950/70 hover:text-amber-950 text-lg leading-none"
          aria-label="Fechar"
        >
          ×
        </button>
      </div>
    </div>
  );
}
